/**
 * navPrefetch — sidebar hover/focus prefetch registry (Tier-1 performance).
 *
 * Maps a left-nav `to` path to its destination page's PRIMARY list query so the
 * Sidebar can warm the React Query cache on user intent (hover/focus) — by the
 * time the click lands the ~200ms round-trip is already paid.
 *
 * CRITICAL invariant: the { queryKey, url } returned here must match EXACTLY
 * what the destination page's own `useQuery` uses in its default (freshly-landed)
 * state — otherwise the prefetch populates a different cache entry and does
 * nothing. The list pages drive their query through `useServerList`, whose
 * composed key + URL are reproduced here:
 *
 *   queryKey = [...baseKey, { page: 0, pageSize, search: '', sortKey, filterKey: '{}' }]
 *   url      = /api/<resource>?page=1&limit=<pageSize>[&sort=<id>&dir=<asc|desc>]
 *
 *   - page 0 (0-based state) → `page=1` on the wire (server pages are 1-based)
 *   - search '' (no `q=`), no active filters (filterKey = JSON.stringify({}) = '{}')
 *   - sortKey / sort+dir present only when the page declares a defaultSort
 *
 * A page reached from the bare nav path always lands in exactly this default
 * state (no deep-link query params), so this is precisely its first query. The
 * one non-`useServerList` entry (Notifications) uses a plain useQuery key/url,
 * reproduced verbatim.
 *
 * Unknown paths return null → the caller no-ops. Prefetch is best-effort.
 */

export interface PrefetchTarget {
  /** Must deep-equal the destination page's useQuery key. */
  queryKey: unknown[];
  /** The exact URL the destination page fetches for its default view. */
  url: string;
}

interface ServerListSpec {
  /** URL slug mounted at /api/<resource> (e.g. 'clients', 'documents/search'). */
  resource: string;
  /** Base react-query key the page passes to useServerList (live params appended). */
  baseKey: readonly unknown[];
  pageSize: number;
  /** Page's defaultSort, or undefined when the page declares none. */
  sort?: { id: string; desc: boolean };
}

function sl(
  resource: string,
  baseKey: readonly unknown[],
  pageSize: number,
  sort?: { id: string; desc: boolean },
): ServerListSpec {
  return { resource, baseKey, pageSize, sort };
}

function resolveServerList(spec: ServerListSpec): PrefetchTarget {
  const { resource, baseKey, pageSize, sort } = spec;
  const sortKey = sort ? `${sort.id}:${sort.desc ? 'desc' : 'asc'}` : '';
  // No active filters on a freshly-landed list → JSON.stringify({}).
  const filterKey = '{}';
  const queryKey = [...baseKey, { page: 0, pageSize, search: '', sortKey, filterKey }];

  const params = new URLSearchParams();
  params.set('page', '1'); // server pages are 1-based (pageIndex 0 → 1)
  params.set('limit', String(pageSize));
  if (sort) {
    params.set('sort', sort.id);
    params.set('dir', sort.desc ? 'desc' : 'asc');
  }
  return { queryKey, url: `/api/${resource}?${params.toString()}` };
}

// route → primary-list query spec. Keys are the exact NavLink `to` paths (App.tsx).
// useServerList-driven list pages: (resource, baseKey, pageSize, defaultSort?).
const SERVER_LIST_ROUTES: Record<string, ServerListSpec> = {
  // Canonical list pages (bespoke useServerList) — ClientsList / ParcelsList /
  // PropertiesList / TaxBillsList / ReturnsList.
  '/clients': sl('clients', ['clients-list'], 25, { id: 'name', desc: false }),
  '/parcels': sl('parcels', ['parcels-list'], 50, { id: 'account_number', desc: false }),
  '/properties': sl('properties', ['properties-list'], 25, { id: 'name', desc: false }),
  '/tax-bills': sl('tax-bills', ['tax-bills-list'], 25, { id: 'due_date', desc: false }),
  '/filed-returns': sl('filed-returns', ['filed-returns-list'], 25, { id: 'tax_year', desc: true }),

  // DocumentsList — bespoke useServerList over the enriched search endpoint
  // (resource 'documents/search', baseKey ['documents-list','search'], 20/page,
  // default sort created_at desc).
  '/documents': sl('documents/search', ['documents-list', 'search'], 20, { id: 'created_at', desc: true }),

  // GenericListByConfig pages — no defaultSort (sortKey ''), 25/page. baseKey is
  // `<apiPath>-list` where apiPath = table with `_` → `-` (GenericList.tsx).
  '/parent-clients': sl('parent-clients', ['parent-clients-list'], 25),
  '/assessors': sl('assessors', ['assessors-list'], 25),
  '/collectors': sl('collectors', ['collectors-list'], 25),
  '/jurisdictions': sl('jurisdictions', ['jurisdictions-list'], 25),
  '/contacts': sl('contacts', ['contacts-list'], 25),
  '/invoices': sl('invoices', ['invoices-list'], 25),
  '/invoice-items': sl('invoice-items', ['invoice-items-list'], 25),
  '/invoice-terms': sl('invoice-terms', ['invoice-terms-list'], 25),
};

// Non-useServerList list routes with a plain useQuery key/url reproduced verbatim.
const PLAIN_ROUTES: Record<string, PrefetchTarget> = {
  // Notifications — GET /api/my-notifications?days=90&limit=200 (Notifications.tsx).
  '/notifications': {
    queryKey: ['notifications-list', { days: 90, limit: 200 }],
    url: '/api/my-notifications?days=90&limit=200',
  },
};

/**
 * Resolve the primary-list prefetch target for a nav `to` path. Returns null for
 * any path not in the registry (the caller then no-ops — prefetch is opt-in per
 * known browsable list route).
 */
export function navPrefetchTarget(path: string): PrefetchTarget | null {
  const spec = SERVER_LIST_ROUTES[path];
  if (spec) return resolveServerList(spec);
  return PLAIN_ROUTES[path] ?? null;
}
